/* eslint react-hooks/rules-of-hooks: 'warn' */

import { Dispatch, FC, SetStateAction } from 'react';
import {
  ChipButton,
  ChipButtonContainer,
  IconArrowDownBold,
  IconGridViewRegular,
  IconThreeGridViewRegular,
  Text,
} from '@croquiscom-pvt/zds';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Log } from '@common/log-manager';

import { useChipAndSortingFilter } from '../../hooks/useChipAndSortingFilter';
import { ChipAndSortingComponentType } from '../../types';
import { ComponentProps } from '../../types/component';
import { FilterParam } from '../../types/param';
import { SortButton } from './sort-button';

interface Props extends ComponentProps<Omit<ChipAndSortingComponentType, 'type'>> {
  onChangeFilter: (params: FilterParam) => void;
  columnCount: 2 | 3;
  setColumnCount: Dispatch<SetStateAction<2 | 3>>;
  log: Partial<Log> & { navigation: string };
  shopId: string;
}

export const ChipAndSortingFilter: FC<React.PropsWithChildren<Props>> = ({
  component,
  columnCount,
  onChangeFilter,
  setColumnCount,
  log,
}) => {
  const { main_title, total_count, sorting_item_list, filter_chip_item_list, column_shifting_button_list } = component;

  const { onChangeSorting, onChangeFilterChip, onChangeGridView } = useChipAndSortingFilter({
    sortComponent: component,
    onChangeFilter,
    setColumnCount,
    log,
  });

  if (!component) {
    return null;
  }

  return (
    <>
      <Wrap className='filter'>
        <Header>
          <Title>
            <Text as='span' variant='Body_18_SemiBold' color='textPrimary'>
              {main_title?.text}
            </Text>
            <Text as='span' variant='Body_15_Medium' color='textQuaternary' style={{ paddingTop: 2 }}>
              {total_count}
            </Text>
          </Title>
          <FilterWrap>
            <SortButtonWrap>
              {sorting_item_list.length > 0 && (
                <SortButton
                  sort_item_list={sorting_item_list}
                  onChange={onChangeSorting}
                  rootCss={css`
                    padding: 0;
                  `}
                  buttonIcon={<IconArrowDownBold size={16} color='iconSecondary' />}
                  buttonCss={css`
                    flex-direction: row-reverse;
                  `}
                />
              )}
            </SortButtonWrap>
            <GridViewButton
              onClick={() => {
                const ubl = column_shifting_button_list?.find((item) => item.column_count === columnCount)?.ubl;
                onChangeGridView(ubl);
              }}
            >
              {columnCount === 2 ? (
                <IconGridViewRegular size={20} color='iconSecondary' />
              ) : (
                <IconThreeGridViewRegular size={20} color='iconSecondary' />
              )}
            </GridViewButton>
          </FilterWrap>
        </Header>
        <ChipButtonContainer.Scrollable>
          {filter_chip_item_list.map(({ id, name, selected, icon, ubl, common_status_text }) => {
            const iconUrl = icon?.webp_url?.normal ?? icon?.url.normal;
            return (
              <ChipButton.Filter
                key={id}
                selected={selected}
                onClick={() => onChangeFilterChip(id, !selected, ubl)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                `}
              >
                {!!iconUrl && <ChipButtonIcon src={iconUrl} alt={`${name} 칩버튼 아이콘 이미지`} />}
                <Text
                  as='span'
                  variant='Body_13_Medium'
                  color={selected ? 'textStrongInverse' : 'textPrimary'}
                  css={css`
                    color: ${selected
                      ? common_status_text?.selected?.color?.normal
                      : common_status_text?.normal?.color?.normal};
                  `}
                >
                  {name ?? ''}
                </Text>
              </ChipButton.Filter>
            );
          })}
        </ChipButtonContainer.Scrollable>
      </Wrap>
    </>
  );
};

const Wrap = styled.section`
  position: relative;
`;

const Header = styled.div`
  margin: 12px 16px 4px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.p`
  display: flex;
  gap: 4px;
`;

const FilterWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SortButtonWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
`;

const GridViewButton = styled.button`
  margin: 0;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

const ChipButtonIcon = styled.img`
  width: 16px;
  height: 16px;
  aspect-ratio: 1;
  object-fit: cover;
  object-position: center;
`;
